import React, { useCallback, useEffect, useRef, useState } from "react"
import API from "../../api"
import { uniqueId } from "lodash"

// redux
import { mapDispatchProps, mapStateToProps } from "../../store/store"
import { connect } from "react-redux"

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from "chart.js"

import Zoom from "chartjs-plugin-zoom"
import { Bar } from "react-chartjs-2"
import Select from "../../components/select"
import { useForceUpdate } from "../../hooks/useForceUpdate"
import { isEmpty } from "../../helpers/object"
import Label from "../../components/label"
import { useLanguage } from "@languages/index"

ChartJS.register(Zoom, CategoryScale, LinearScale, PointElement, BarElement, Title, Tooltip, Legend)

const ModelActivityWeeklyIndex = (props: any) => {
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.charts }

  const durations = [
    { name: languageTexts.weekly_text_1, value: 1 },
    { name: languageTexts.weekly_text_2, value: 2 },
    { name: languageTexts.weekly_text_3, value: 3 },
    { name: languageTexts.weekly_text_4, value: 4 },
    { name: languageTexts.weekly_text_5, value: 5 },
    { name: languageTexts.weekly_text_6, value: 6 },
    { name: languageTexts.weekly_text_7, value: 7 },
    { name: languageTexts.weekly_text_8, value: 8 },
    { name: languageTexts.weekly_text_9, value: 9 },
    { name: languageTexts.weekly_text_10, value: 10 },
  ]

  const [currentDuration, setCurrentDuration] = useState<any>(durations[0])
  const forceUpdate = useForceUpdate()

  const options = {
    responsive: true,
    maintainAspectRatio: false,

    scales: {
      y: {
        beginAtZero: true,
      },
    },

    plugins: {
      legend: {
        display: false,
        labels: {
          usePointStyle: true,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || ""

            if (label) {
              label += ": "
            }
            if (context.parsed.y !== null) {
              label += languageTexts._num_of_person(context.parsed.y)
            }
            return label
          },
        },
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: "x" as "x",
        },
        pan: {
          enabled: true,
          mode: "x" as "x",
        },
      },
    },

    onClick: function (evt, element) {
      if (element.length > 0) {
        const index = element[0].index
        const value = element[0].element.$context.raw
      }
    },
  }

  const RentalMasterRetentionChart = (props: any) => {
    if (!props.currentDuration) {
      return <></>
    }

    const [chartData, setChartData] = useState<any>()

    const labels = useRef<Array<any>>()
    const usersFound = useRef<Array<any>>()
    const users = useRef<number>()

    const fetchChartData = () => {
      API.Chart.index({
        chart: "MODELS-RENTAL-MASTER-WEEKLY",
        weeks_after_approved: currentDuration.value,
      })
        .then((response) => {
          const values = response.data.chart

          let chart = {
            data: values,
            borderColor: "rgb(255, 188, 100)",
            borderWidth: 0,
            backgroundColor: "rgb(255, 188, 100)",
            pointStyle: "circle",
            pointRadius: 4,
            pointBorderColor: "rgb(0, 0, 0)",
          }

          usersFound.current = response.data.usersFound
          labels.current = response.data.labels
          users.current = response.data.users

          setChartData({
            labels: response.data.labels,
            tension: 0.5,
            datasets: [chart],
          })
        })
        .catch((error) => {
          console.log("error", error)
          props.showModal({
            text: languageTexts.weekly_text_11,
          })
        })
    }

    useEffect(() => {
      fetchChartData()
    }, [])

    const render = () => {
      return !labels.current ? (
        <div className="flex flex-row mx-10 my-10 justify-center items-center">
          <div className="flex flex-row justify-center text-center my-5">
            <span className="prose prose-xl text-slate-400">{languageTexts.loading_1}</span>
          </div>
        </div>
      ) : (
        <div className="px-5 md:w-[800px] xl:w-[1024px] mx-auto">
          <div className="flex flex-row px-5">
            <div className="flex flex-col mb-4">
              <span className="text-xl font-bold tracking-tighter">{languageTexts.approved_at_text_8}</span>
              <span className="text-sm tracking-tighter text-gray-400">
                {languageTexts._approved_at_text_3(users.current)}
              </span>
            </div>
          </div>

          <div className="flex flex-row px-5 gap-3">
            {chartData && !isEmpty(usersFound.current)
              ? Object.values(usersFound.current).map((weeks, index) => {
                  return (
                    <div key={`foundUsers${uniqueId()}`} className="flex flex-col gap-1">
                      <span className="text-md tracking-tighter font-bold">
                        {languageTexts._weekly_text_12(index + 1, JSON.stringify(weeks))}
                      </span>
                      <Label
                        text={
                          Object.values(usersFound.current).length - 1 == index
                            ? "100.00%"
                            : `${(
                                (Object.values(usersFound.current)
                                  .filter((arr, arrIndex) => arrIndex <= index)
                                  .reduce(function add(sum, currValue) {
                                    return sum + currValue
                                  }, 0) /
                                  users.current) *
                                100
                              ).toFixed(2)}% `
                        }
                      />
                    </div>
                  )
                })
              : null}
          </div>

          <div className="flex flex-row">{chartData ? <Bar options={options} data={chartData} /> : null}</div>
        </div>
      )
    }

    return render()
  }

  const RentalRetentionChart = (props: any) => {
    if (!props.currentDuration) {
      return <></>
    }

    const [chartData, setChartData] = useState<any>()

    const labels = useRef<Array<any>>()
    const usersFound = useRef<Array<any>>()
    const users = useRef<number>()

    const fetchChartData = () => {
      API.Chart.index({
        chart: "MODELS-RENTAL-WEEKLY",
        weeks_after_approved: currentDuration.value,
      })
        .then((response) => {
          const values = response.data.chart

          let chart = {
            data: values,
            borderColor: "rgb(255, 188, 100)",
            borderWidth: 0,
            backgroundColor: "rgb(255, 188, 100)",
            pointStyle: "circle",
            pointRadius: 4,
            pointBorderColor: "rgb(0, 0, 0)",
          }

          usersFound.current = response.data.usersFound
          labels.current = response.data.labels
          users.current = response.data.users

          setChartData({
            labels: response.data.labels,
            tension: 0.5,
            datasets: [chart],
          })
        })
        .catch((error) => {
          console.log("error", error)

          props.showModal({
            text: languageTexts.weekly_text_11,
          })
        })
    }

    useEffect(() => {
      fetchChartData()
    }, [])

    const render = () => {
      return !labels.current ? (
        <div className="flex flex-row mx-10 my-10 justify-center items-center">
          <div className="flex flex-row justify-center text-center my-5">
            <span className="prose prose-xl text-slate-400">{languageTexts.loading_1}</span>
          </div>
        </div>
      ) : (
        <div className="px-5 md:w-[800px] xl:w-[1024px] mx-auto">
          <div className="flex flex-row px-5">
            <div className="flex flex-col mb-4">
              <span className="text-xl font-bold tracking-tighter">{languageTexts.approved_at_text_5}</span>
              <span className="text-sm tracking-tighter text-gray-400">
                {languageTexts._approved_at_text_3(users.current)}
              </span>
            </div>
          </div>

          <div className="flex flex-row">{chartData ? <Bar options={options} data={chartData} /> : null}</div>
        </div>
      )
    }

    return render()
  }

  const CartRetentionChart = (props: any) => {
    if (!props.currentDuration) {
      return <></>
    }

    const [chartData, setChartData] = useState<any>()

    const labels = useRef<Array<any>>()
    const usersFound = useRef<Array<any>>()
    const users = useRef<number>()

    const fetchChartData = () => {
      API.Chart.index({
        chart: "MODELS-CART-WEEKLY",
        weeks_after_approved: currentDuration.value,
      })
        .then((response) => {
          const values = response.data.chart

          let chart = {
            data: values,
            borderColor: "rgb(255, 188, 100)",
            borderWidth: 0,
            backgroundColor: "rgb(255, 188, 100)",
            pointStyle: "circle",
            pointRadius: 4,
            pointBorderColor: "rgb(0, 0, 0)",
          }

          usersFound.current = response.data.usersFound
          labels.current = response.data.labels
          users.current = response.data.users

          setChartData({
            labels: response.data.labels,
            tension: 0.5,
            datasets: [chart],
          })
        })
        .catch((error) => {
          console.log("error", error)

          props.showModal({
            text: languageTexts.weekly_text_11,
          })
        })
    }

    useEffect(() => {
      fetchChartData()
    }, [])

    const render = () => {
      return !labels.current ? (
        <div className="flex flex-row mx-10 my-10 justify-center items-center">
          <div className="flex flex-row justify-center text-center my-5">
            <span className="prose prose-xl text-slate-400">{languageTexts.loading_1}</span>
          </div>
        </div>
      ) : (
        <div className="px-5 md:w-[800px] xl:w-[1024px] mx-auto">
          <div className="flex flex-row px-5">
            <div className="flex flex-col mb-4">
              <span className="text-xl font-bold tracking-tighter">{languageTexts.weekly_text_13}</span>
              <span className="text-sm tracking-tighter text-gray-400">
                {languageTexts._approved_at_text_3(users.current)}
              </span>
            </div>
          </div>

          <div className="flex flex-row px-5 gap-3">
            {chartData && !isEmpty(usersFound.current)
              ? Object.values(usersFound.current).map((weeks, index) => {
                  return (
                    <div key={`foundUsers${uniqueId()}`} className="flex flex-col gap-1">
                      <span className="text-md tracking-tighter font-bold">
                        {languageTexts._weekly_text_12(index + 1, JSON.stringify(weeks))}
                      </span>
                      <Label
                        text={
                          Object.values(usersFound.current).length - 1 == index
                            ? "100.00%"
                            : `${(
                                (Object.values(usersFound.current)
                                  .filter((arr, arrIndex) => arrIndex <= index)
                                  .reduce(function add(sum, currValue) {
                                    return sum + currValue
                                  }, 0) /
                                  users.current) *
                                100
                              ).toFixed(2)}% `
                        }
                      />
                    </div>
                  )
                })
              : null}
          </div>

          <div className="flex flex-row">{chartData ? <Bar options={options} data={chartData} /> : null}</div>
        </div>
      )
    }

    return render()
  }

  const ModelRentalStatistics = (props: any) => {
    const [statistics, setStatistics] = useState<any>()

    useEffect(() => {
      fetchStatistics()
    }, [])

    const fetchStatistics = () => {
      API.Chart.modelRentalStatistics({
        weeks_after_approved: currentDuration.value,
      })
        .then((response) => {
          setStatistics(response.data)
        })
        .catch((error) => {})
    }

    const render = useCallback(() => {
      return statistics ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-row px-5">
            <div className="flex flex-col mb-4">
              <span className="text-xl font-bold tracking-tighter">{languageTexts.approved_at_text_9}</span>
              <span className="text-sm tracking-tighter text-gray-400">
                {languageTexts._num_of_person(statistics.models)}
              </span>
            </div>
          </div>

          <div className="flex flex-row px-5">
            <div className="flex flex-col mb-4">
              <span className="text-xl font-bold tracking-tighter">{languageTexts.approved_at_text_10}</span>
              <span className="text-sm tracking-tighter ">
                {languageTexts._num_of_person(statistics.activeUsersOneMonth)} (
                {((statistics.activeUsersOneMonth / statistics.models) * 100).toFixed(2)}
                %)
              </span>
            </div>
          </div>

          <div className="flex flex-row px-5">
            <div className="flex flex-col mb-4">
              <span className="text-xl font-bold tracking-tighter">{languageTexts.approved_at_text_11}</span>
              <span className="text-sm tracking-tighter ">
                {languageTexts._num_of_person(statistics.activeUsersTwoMonth)} (
                {((statistics.activeUsersTwoMonth / statistics.models) * 100).toFixed(2)}
                %)
              </span>
            </div>
          </div>

          <div className="flex flex-row px-5">
            <div className="flex flex-col mb-4">
              <span className="text-xl font-bold tracking-tighter">{languageTexts.approved_at_text_12}</span>
              <span className="text-sm tracking-tighter ">
                {languageTexts._num_of_person(statistics.activeUsersThreeMonth)} (
                {((statistics.activeUsersThreeMonth / statistics.models) * 100).toFixed(2)}
                %)
              </span>
            </div>
          </div>

          <div className="flex flex-row px-5">
            <div className="flex flex-col mb-4">
              <span className="text-xl font-bold tracking-tighter">
                {languageTexts._weekly_text_14(currentDuration?.value)}
              </span>
              <span className="text-sm tracking-tighter ">
                {languageTexts._num_of_person(statistics.modelsWeeksAfter)}
              </span>
            </div>
          </div>
        </div>
      ) : null
    }, [statistics])
    return render()
  }
  useEffect(() => {
    forceUpdate()
  }, [currentDuration])

  const render = useCallback(() => {
    return (
      <div className="flex flex-col my-10 gap-10">
        <div className="px-5 md:w-[800px] xl:w-[1024px] mx-auto">
          <div className="flex flex-col my-5 px-5">
            <span className="text-3xl font-bold tracking-tighter text-center">
              {languageTexts._weekly_text_15(currentDuration.value)}
            </span>
          </div>
        </div>

        <div className="px-5 md:w-[800px] xl:w-[1024px] mx-auto">
          <div className="flex flex-col my-10 px-5">
            <span className="text-xl font-bold tracking-tighter">{languageTexts.weekly_text_16}</span>

            <Select
              bordered
              options={durations}
              selected={currentDuration?.name ?? ""}
              value={currentDuration?.name ?? ""}
              onChange={(value) => {
                const selected = durations.filter((duration) => {
                  return duration.name == value
                })[0]

                setCurrentDuration(selected)

                console.log("selected", selected)
              }}
            />
          </div>

          <ModelRentalStatistics currentDuration={currentDuration} />
        </div>

        <div className="flex flex-col gap-5">
          <CartRetentionChart currentDuration={currentDuration} />
          <RentalMasterRetentionChart currentDuration={currentDuration} />
          <RentalRetentionChart currentDuration={currentDuration} />
        </div>
      </div>
    )
  }, [currentDuration])

  return render()
}

export default connect(mapStateToProps, mapDispatchProps)(ModelActivityWeeklyIndex)
